import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import Container from "@mui/material/Container";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import HistoryIcon from "@mui/icons-material/History";

import CommonLayout from "../../Common/Layout";
import HelpContextIcon from "../../Common/HelpPanel/HelpContextIcon";
import { navAppHosting } from "../../kmk/AppData";
import KMKTable from "../../Common/Table/KMKTable";
import { ButtonUI, ConfirmPopup } from "../../Common";
import { setPrevState } from "../../../util/common";
import { showToast } from "../../../store/actions";
import App from "../../../service/appService";
import useWindowResize from "../../../hooks/useWindowsReszie";
import AppHistoryPopup from "./AppHistory";

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    backgroundColor: "#e8edf1",
    color: "black",
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    display: "flex",
    flexWrap: "wrap",
    padding: 0,
  },
  HeaderTitle: {
    fontSize: 20,
    whiteSpace: "nowrap",
  },
  PanelContainer: {
    padding: "10px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "52px",
    },
  },
  flexBox: {
    display: "flex",
    alignContent: "center",
    alignItems: "center",
  },
  disabledIconButton: {
    color: "#00000042",
  },
  IconHolder: {
    padding: 0,
    margin: "0 2px",
    fontSize: "22px",
    height: "32px",
    width: "32px",
    borderRadius: "64px",
  },
}));

const POPUPDETAILS = {
  deploy: {
    title: "Deploy Application",
    message: "Are you sure you want to deploy APPNAME application?",
    btnName: "Deploy",
    btnColor: "success",
  },
  redeploy: {
    title: "Redeploy Application",
    message: "Are you sure you want to redeploy APPNAME application?",
    btnName: "Redeploy",
    btnColor: "success",
  },
  undeploy: {
    title: "Undeploy Application",
    message: "Are you sure you want to undeploy APPNAME application?",
    btnName: "Ok",
    btnColor: "success",
  },
  delete: {
    title: "Delete Application",
    message: "Are you sure you want to delete APPNAME application?",
    btnName: "Ok",
    btnColor: "success",
  },
};

const AppHosting = () => {
  const { Workspace_id } = useParams();

  const navigate = useNavigate();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { height: tableHeight } = useWindowResize(170);

  const workspace = useSelector((state) => state.workspace);
  const { appImageConfig } = useSelector((state) => state.config);
  const { Name: workspaceName, accessRole } = workspace.workspace;

  const [loading, setLoading] = useState(false);
  const [appsData, setAppsData] = useState([]);

  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    App_id: null,
    AppName: "",
    actionType: "deploy",
    loadingForConfirm: false,
  });
  const { open, App_id, AppName, actionType, loadingForConfirm } = confirmPopup;

  const [placeHolderSearch, setPlaceHolderSearch] = useState("");
  const [options, setOptions] = useState({
    orderBy: "NAME",
    order: "asc",
    search: "",
  });

  const [historyPopup, setHistoryPopup] = useState({
    open: false,
    App_id: null,
    AppName: "",
  });

  const { orderBy, order, search } = options;
  const listingRef = useRef(options);

  const handleAddClick = useCallback(
    (id = null) => {
      navigate(`/my-workspaces/${Workspace_id}/app-hosting/${id || "add"}`);
    },
    [Workspace_id, navigate]
  );

  const getAppList = useCallback(
    async (options = {}) => {
      setLoading(true);

      const apps = new App();
      const { success, data } = await apps.getAppList(Workspace_id, {
        orderBy: listingRef.current.orderBy,
        order: listingRef.current.order,
        search: listingRef.current.search,
        ...options,
      });

      if (success) setAppsData(data);
      setLoading(false);
    },
    [Workspace_id]
  );

  const updateListingRefObj = useCallback(
    (passedOptions) => {
      listingRef.current = { search, orderBy, order, ...passedOptions };
      getAppList(passedOptions);
    },
    [getAppList, order, orderBy, search]
  );

  const handleSearch = useCallback(
    (e) => {
      if (e.key === "Enter" && search !== placeHolderSearch) {
        const search = placeHolderSearch;
        setOptions((prev) => ({ ...prev, search }));
        updateListingRefObj({ search });
      }
    },
    [placeHolderSearch, search, updateListingRefObj]
  );

  const handleClearSearch = useCallback(() => {
    setPlaceHolderSearch("");
    setOptions((prev) => ({ ...prev, search: "" }));
    updateListingRefObj({ search: "" });
  }, [updateListingRefObj]);

  const handleSorting = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === "asc";
      let tmpOrder = isAsc ? "desc" : "asc";

      setPrevState(setOptions, { order: tmpOrder, orderBy: property });
      updateListingRefObj({ orderBy: property, order: tmpOrder });
    },
    [order, orderBy, updateListingRefObj]
  );

  const handleCancel = useCallback(() => {
    setConfirmPopup({ open: false, App_id: null, status: null, loadingForConfirm: false });
  }, []);

  const handleDelete = useCallback(async () => {
    setPrevState(setConfirmPopup, { loadingForConfirm: true });
    const apps = new App();
    const { success, message } = await apps.deleteApp(Workspace_id, App_id);
    if (success) {
      dispatch(showToast({ message, type: "success" }));
      getAppList();
    }
    handleCancel();
  }, [App_id, Workspace_id, dispatch, getAppList, handleCancel]);

  const handleDeploy = useCallback(async () => {
    setPrevState(setConfirmPopup, { loadingForConfirm: true });
    const apps = new App();
    const { success, message } = await apps.deployApp(Workspace_id, App_id);
    if (success) {
      dispatch(showToast({ message, type: "success" }));
      getAppList();
    }
    handleCancel();
  }, [App_id, Workspace_id, dispatch, getAppList, handleCancel]);

  const handleUndeploy = useCallback(async () => {
    setPrevState(setConfirmPopup, { loadingForConfirm: true });
    const apps = new App();
    const { success, message } = await apps.undeployApp(Workspace_id, App_id);
    if (success) {
      dispatch(showToast({ message, type: "success" }));
      getAppList();
    }
    handleCancel();
  }, [App_id, Workspace_id, dispatch, getAppList, handleCancel]);

  const handleRedeploy = useCallback(async () => {
    setPrevState(setConfirmPopup, { loadingForConfirm: true });
    const apps = new App();
    const { success, message } = await apps.redeployApp(Workspace_id, App_id);
    if (success) {
      dispatch(showToast({ message, type: "success" }));
      getAppList();
    }
    handleCancel();
  }, [App_id, Workspace_id, dispatch, getAppList, handleCancel]);

  const handleClick = useCallback(async () => {
    if (actionType === "delete") await handleDelete();
    if (actionType === "deploy") await handleDeploy();
    if (actionType === "undeploy") await handleUndeploy();
    if (actionType === "redeploy") await handleRedeploy();
  }, [actionType, handleDelete, handleDeploy, handleUndeploy, handleRedeploy]);

  const handleCellClick = useCallback(
    (record, colId) => {
      if (colId === "ACCESS_URL" && record.Status === "Deployed")
        window.open(record.Access_URL + "/", "_blank", "noopener,noreferrer");
      else handleAddClick(record.App_id);
    },
    [handleAddClick]
  );

  const HostActions = useCallback(
    ({ App_id, AppName, status }) => {
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Box sx={{ minHeight: 37, minWidth: 37, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Tooltip title="View Logs" arrow>
              <IconButton
                aria-label="View Logs"
                onClick={(e) => {
                  e.stopPropagation();
                  setHistoryPopup({
                    open: true,
                    App_id: App_id,
                    AppName: AppName,
                  });
                }}
                sx={{ color: "#0288D1" }}
                className={classes.IconHolder}
              >
                <HistoryIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ minHeight: 37, minWidth: 37, display: "flex", justifyContent: "center", alignItems: "center" }}>
            {["Not Deployed"].includes(status) && (
              <Tooltip title="Deploy" arrow>
                <IconButton
                  aria-label="Deploy"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPrevState(setConfirmPopup, { open: true, App_id, AppName, actionType: "deploy" });
                  }}
                  style={{ color: "#1565c0" }}
                  className={classes.IconHolder}
                >
                  <PlayCircleOutlineOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            {["Deployed", "Deployment Failed", "Undeployment Failed"].includes(status) && (
              <Tooltip title="Redeploy" arrow>
                <IconButton
                  aria-label="Redeploy"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPrevState(setConfirmPopup, { open: true, App_id, AppName, actionType: "redeploy" });
                  }}
                  color="info"
                  className={classes.IconHolder}
                >
                  <PublishedWithChangesOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ minHeight: 37, minWidth: 37, display: "flex", justifyContent: "center", alignItems: "center" }}>
            {["Deploying", "Deployed", "Deployment Failed", "Undeployment Failed"].includes(status) && (
              <Tooltip title="Undeploy" arrow>
                <IconButton
                  aria-label="Undeploy"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPrevState(setConfirmPopup, { open: true, App_id, AppName, actionType: "undeploy" });
                  }}
                  color="warning"
                  className={classes.IconHolder}
                >
                  <StopCircleOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            {["Not Deployed"].includes(status) && (
              <Tooltip title="Delete" arrow>
                <IconButton
                  aria-label="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPrevState(setConfirmPopup, { open: true, App_id, AppName, actionType: "delete" });
                  }}
                  color="error"
                  className={classes.IconHolder}
                >
                  <DeleteOutlineOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </div>
      );
    },
    [classes.IconHolder]
  );

  const appsColumns = useMemo(
    () => [
      {
        id: "NAME",
        name: "NAME",
        style: { maxWidth: 290, wordBreak: "break-word" },
        width: 150,
        align: "left",
        sortable: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => record?.Name || "",
      },
      {
        id: "TYPE",
        name: "TYPE",
        style: { maxWidth: 290, wordBreak: "break-word" },
        width: 120,
        align: "left",
        sortable: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          const Type = appImageConfig?.find((app) => app.type === record.Type);
          if (Type) {
            return (
              <Box className={classes.flexBox} sx={{ maxWidth: "fit-content" }}>
                <img src={`/images/${Type?.icon}`} alt={Type?.type} style={{ width: "20px", marginRight: "10px" }} />
                {Type?.type}
              </Box>
            );
          }
          return "";
        },
      },
      {
        id: "STATUS",
        name: "STATUS",
        style: { maxWidth: 290, wordBreak: "break-word" },
        width: 120,
        align: "left",
        sortable: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => record?.Status || "",
      },
      {
        id: "Deployed_By",
        name: "DEPLOYED BY",
        style: { maxWidth: 290, wordBreak: "break-word" },
        width: 150,
        align: "left",
        sortable: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => record?.Deployed_By || "",
      },
      {
        id: "ACCESS_URL",
        name: "ACCESS URL",
        style: { maxWidth: 290, wordBreak: "break-word" },
        width: 200,
        align: "left",
        sortable: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          if (record.Access_URL) {
            const linkToRedirect = `${record.Access_URL}/`;
            if (record.Status === "Deployed") return <Link>{linkToRedirect}</Link>;
            else return linkToRedirect;
          }
          return "";
        },
      },
      {
        id: "ACTIONS",
        name: "ACTIONS",
        style: { maxWidth: 90, wordBreak: "break-word" },
        width: 90,
        align: "center",
        sortable: false,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) =>
          [1].includes(accessRole) && (
            <HostActions
              App_id={record.App_id}
              AppName={record?.Name || ""}
              status={record?.Status || ""}
            ></HostActions>
          ),
      },
    ],
    [accessRole, appImageConfig, classes.flexBox]
  );

  useEffect(() => {
    getAppList();
    return () => {};
  }, [getAppList]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
      { link: "#", displayName: navAppHosting.name, active: true },
    ],
    [Workspace_id, workspaceName]
  );

  return (
    <CommonLayout title={navAppHosting.name} breadcrumbArr={breadcrumbArr}>
      <Container maxWidth="false" sx={{ m: 0, height: "100%", overflowY: "auto" }} className={classes.PanelContainer}>
        <Toolbar className={classes.Header}>
          <Box sx={{ display: "flex", flex: "1", alignItems: "center", pr: "16px", mb: 1 }}>
            <Typography className={classes.HeaderTitle} component="div" sx={{ paddingRight: "16px" }}>
              Applications
            </Typography>
            {[1].includes(accessRole) && (
              <Box>
                <ButtonUI endIcon={<AddIcon />} variant="contained" color="info" onClick={() => handleAddClick()}>
                  Add Application
                </ButtonUI>
              </Box>
            )}
            <HelpContextIcon screen={navAppHosting.name} />
          </Box>
          <Box sx={{ marginLeft: "auto" }}>
            <TextField
              margin="normal"
              value={placeHolderSearch}
              onKeyDown={handleSearch}
              onChange={(e) => {
                setPlaceHolderSearch(e.target.value);
                if (e.target.value === "") setOptions({ ...options, search: e.target.value });
              }}
              placeholder="Search..."
              variant="standard"
              inputProps={{ maxLength: 50 }}
              InputProps={{
                startAdornment: <SearchIcon color="primary" />,
                ...(placeHolderSearch && {
                  endAdornment: (
                    <IconButton onClick={handleClearSearch} size="small" sx={{ p: 0 }}>
                      <ClearIcon color="primary" />
                    </IconButton>
                  ),
                }),
              }}
              sx={{ m: 0, width: "250px", ml: "4px", mr: "4px" }}
              disabled={loading}
            />
            <Tooltip placement="top" title="Refresh" arrow>
              <span>
                <IconButton disabled={loading || loadingForConfirm} onClick={getAppList}>
                  <RefreshOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Toolbar>

        <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
          <KMKTable
            loading={loading}
            columns={appsColumns}
            containerHeight={tableHeight}
            sortingProperties={{ orderBy, order, handleSorting }}
            paginationProperties={{}}
            noDataText="No data available"
            data={appsData}
            boldHeader
            tableProps={{ size: "medium", stickyHeader: true }}
            dataIdKey="App_id"
            onCellClick={handleCellClick}
          />
        </Paper>
      </Container>
      {open && (
        <ConfirmPopup
          configData={{
            popupTitle: POPUPDETAILS[actionType]?.title,
            popupMessage: (
              <Box sx={{ maxWidth: "400px" }}>
                {POPUPDETAILS[actionType]?.message.replace("APPNAME", `"${AppName}"`)}
              </Box>
            ),
            handleCancel,
            handleClick,
            loadingForConfirm,
            confirmBtnColor: POPUPDETAILS[actionType]?.btnColor,
            confirmBtnName: POPUPDETAILS[actionType]?.btnName,
          }}
        />
      )}
      {historyPopup.open && (
        <AppHistoryPopup
          handleCancel={() => setPrevState(setHistoryPopup, { open: false })}
          popupTitle={`"${historyPopup.AppName}" History`}
          appId={historyPopup.App_id}
        />
      )}
    </CommonLayout>
  );
};

export default AppHosting;
